/**
 * Centralized autosave: debounce edits (both document text and highlights),
 * and expose a `flushAutosave()` that anything switching the active document
 * (Open/New/Save As/import) or the tab going away must call first — a bare
 * debounce timer alone doesn't survive either of those, and previously
 * nothing flushed it, so up to SAVE_DEBOUNCE_MS of edits could be silently
 * dropped when the document was swapped out from under it.
 *
 * Text and highlights are independent change sources (a highlight can be
 * added by the plugin over RPC with no editor keystroke involved), so they
 * get their own debounce timers, but share one status so the UI reflects
 * "is anything unsaved" rather than two separate indicators.
 */

import { create } from "zustand";
import type { Editor } from "@tiptap/react";
import { persistFromEditorState, persistHighlights } from "./persist";
import { useHighlightStore } from "../store/highlightStore";
import { getSettingValue } from "../settings/settingsStore";
import type { Highlight } from "../types";

/** Re-read on every schedule call so a mid-session Settings change applies immediately. */
function saveDebounceMs(): number {
  const value = getSettingValue("general", "autosaveDelayMs");
  return typeof value === "number" && value > 0 ? value : 500;
}

export type AutosaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

interface AutosaveStore {
  status: AutosaveStatus;
  error: string | null;
}

export const useAutosaveStore = create<AutosaveStore>(() => ({
  status: "idle",
  error: null,
}));

let textTimer: ReturnType<typeof setTimeout> | null = null;
let pendingEditor: Editor | null = null;
let highlightTimer: ReturnType<typeof setTimeout> | null = null;
let pendingHighlights: Highlight[] | null = null;

function markPending(): void {
  useAutosaveStore.setState({ status: "pending", error: null });
}

/** Only settle to "saved" once nothing else is still queued or in flight. */
function settleSaved(): void {
  if (!textTimer && !highlightTimer) useAutosaveStore.setState({ status: "saved", error: null });
}

function settleError(err: unknown): void {
  useAutosaveStore.setState({
    status: "error",
    error: err instanceof Error ? err.message : String(err),
  });
}

/** Debounced save — call on every editor transaction that changes the doc. */
export function scheduleAutosave(editor: Editor): void {
  pendingEditor = editor;
  if (textTimer) clearTimeout(textTimer);
  markPending();
  textTimer = setTimeout(() => {
    textTimer = null;
    void flushText();
  }, saveDebounceMs());
}

function scheduleHighlightAutosave(highlights: Highlight[]): void {
  pendingHighlights = highlights;
  if (highlightTimer) clearTimeout(highlightTimer);
  markPending();
  highlightTimer = setTimeout(() => {
    highlightTimer = null;
    void flushHighlights();
  }, saveDebounceMs());
}

// Set up once: any highlight add/update/remove/set/clear schedules a save,
// the same way Editor.tsx's onUpdate schedules a text save. Compares
// references so unrelated store fields (loading/error) don't trigger writes.
useHighlightStore.subscribe((state, prevState) => {
  if (state.highlights === prevState.highlights) return;
  scheduleHighlightAutosave(state.highlights);
});

async function flushText(): Promise<void> {
  if (textTimer) {
    clearTimeout(textTimer);
    textTimer = null;
  }
  const editor = pendingEditor;
  pendingEditor = null;
  if (!editor || editor.isDestroyed) {
    settleSaved();
    return;
  }
  useAutosaveStore.setState({ status: "saving" });
  try {
    await persistFromEditorState(editor.state);
    settleSaved();
  } catch (err) {
    settleError(err);
    throw err;
  }
}

async function flushHighlights(): Promise<void> {
  if (highlightTimer) {
    clearTimeout(highlightTimer);
    highlightTimer = null;
  }
  const highlights = pendingHighlights;
  pendingHighlights = null;
  if (highlights === null) {
    settleSaved();
    return;
  }
  useAutosaveStore.setState({ status: "saving" });
  try {
    await persistHighlights(highlights);
    settleSaved();
  } catch (err) {
    settleError(err);
    throw err;
  }
}

/**
 * Immediately persists any pending debounced edit (text and highlights).
 * Safe to call with nothing pending. Also used directly by the manual
 * File > Save action.
 */
export async function flushAutosave(): Promise<void> {
  await Promise.all([flushText(), flushHighlights()]);
}

/**
 * Drop any queued debounced save WITHOUT writing it. Used when disk state is
 * being force-loaded over the in-memory copy — specifically after cloud sync
 * downloads a newer remote document (see tabs.reloadActiveDocumentFromDisk).
 * There, a flush would serialize the now-stale in-memory editor and write it
 * back over the file we just downloaded, so we discard the pending edit
 * instead. This is the last-writer-wins tradeoff the sync layer already makes:
 * the remote won the mtime comparison, so the local unsaved edit is dropped.
 */
export function cancelPendingAutosave(): void {
  if (textTimer) {
    clearTimeout(textTimer);
    textTimer = null;
  }
  if (highlightTimer) {
    clearTimeout(highlightTimer);
    highlightTimer = null;
  }
  pendingEditor = null;
  pendingHighlights = null;
  useAutosaveStore.setState({ status: "idle", error: null });
}
